import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, setDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import RecipeVault from './RecipeVault';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import GroceryDrawer from './GroceryDrawer';

const MealPlanner = () => {
  const [meals, setMeals] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeForVault, setSelectedRecipeForVault] = useState(null);
  const [isGroceryOpen, setIsGroceryOpen] = useState(false);
  const [staples, setStaples] = useState([]);

  // Fetch recipes
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'recipes'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRecipes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    
    return () => unsubscribe();
  }, [user]);

  // Fetch staples 
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, `users/${user.uid}/staples`));
    return onSnapshot(q, (snapshot) => {
      setStaples(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, [user]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside or no movement
    if (!destination) return;

    const targetDateId = destination.droppableId.replace('day-', '');

    // Case 1: Dragging from Recipe Vault to a Day
    if (source.droppableId === 'recipe-vault' && destination.droppableId.startsWith('day-')) {
      const recipe = recipes.find(r => r.id === draggableId);
      if (recipe) {
        await handleSelectRecipe(targetDateId, recipe);
      }
      return;
    }

    // Case 2: Swapping/Moving between days
    if (source.droppableId.startsWith('day-') && destination.droppableId.startsWith('day-')) {
      const sourceDateId = source.droppableId.replace('day-', '');
      
      // Don't do anything if dropped onto the same exact spot
      if (sourceDateId === targetDateId) return;

      const sourceMeal = meals[sourceDateId] || { main: '', side: '', recipeId: null, ingredients: [] };
      const targetMeal = meals[targetDateId] || { main: '', side: '', recipeId: null, ingredients: [] };

      // Optimistic local state updates to prevent UI lag
      setMeals(prev => ({
        ...prev,
        [sourceDateId]: { ...targetMeal, date: sourceDateId },
        [targetDateId]: { ...sourceMeal, date: targetDateId }
      }));

      // Persist changes to Firebase
      try {
        const sourceDocId = `${user.uid}_${sourceDateId}`;
        const targetDocId = `${user.uid}_${targetDateId}`;

        await Promise.all([
          setDoc(doc(db, "meals", sourceDocId), {
            main: targetMeal.main || "",
            side: targetMeal.side || "",
            recipeId: targetMeal.recipeId || null,
            ingredients: targetMeal.ingredients || [],
            date: sourceDateId,
            userId: user.uid
          }, { merge: true }),
          
          setDoc(doc(db, "meals", targetDocId), {
            main: sourceMeal.main || "",
            side: sourceMeal.side || "",
            recipeId: sourceMeal.recipeId || null,
            ingredients: sourceMeal.ingredients || [],
            date: targetDateId,
            userId: user.uid
          }, { merge: true })
        ]);
      } catch (err) {
        console.error("Failed to swap meals across days:", err);
      }
    }
  };

  const getInitialSaturday = () => {
    const d = new Date();
    // Adjust to previous Saturday
    d.setDate(d.getDate() - ((d.getDay() + 1) % 7)); 
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const [currentWeekStart, setCurrentWeekStart] = useState(getInitialSaturday());
  const [isVaultOpen, setIsVaultOpen] = useState(false);

  // Helper to get YYYY-MM-DD for any date object
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeekDates = useCallback(() => {
    return [...Array(7)].map((_, i) => {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + i);
      return formatDate(d);
    });
  }, [currentWeekStart]);

  useEffect(() => {
    if (!user) return;

    const dates = getWeekDates();
    // Define the query correctly
    const q = query(
      collection(db, "meals"), 
      where('userId', '==', user.uid),
      where('date', '>=', dates[0]),
      where('date', '<=', dates[6])
    );

    // Replace your onSnapshot block with this to see errors in the console:
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const mealsMap = {};
        snapshot.forEach(doc => {
          mealsMap[doc.data().date] = doc.data();
        });
        setMeals(mealsMap);
        setLoading(false);
      }, 
      (error) => {
        console.error("Meal Query Failed:", error);
        // Even if it fails, stop loading so the user sees the UI
        setLoading(false); 
      }
    );

    return () => unsubscribe();
  }, [user, getWeekDates]);

  const handleChange = async (dateId, field, value) => {
    // 1. Update LOCAL state immediately
    setMeals(prev => ({
      ...prev,
      [dateId]: {
        ...prev[dateId],
        [field]: value,
        date: dateId
      }
    }));

    // 2. Sync to Firebase
    try {
      const docId = `${user.uid}_${dateId}`;
      await setDoc(doc(db, "meals", docId), {
        [field]: value,
        date: dateId,
        userId: user.uid
      }, { merge: true });
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const handleNextWeek = () => {
    const next = new Date(currentWeekStart);
    next.setDate(next.getDate() + 7);
    setCurrentWeekStart(next);
  };

  const handlePrevWeek = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeekStart(prev);
  };

  const handleSelectRecipe = async (dateId, recipe) => {
    // 1. Update the main dish title
    await handleChange(dateId, 'main', recipe.title);
    
    // 2. Prepare the metadata, ensuring no 'undefined' values
    const docId = `${user.uid}_${dateId}`;
    
    const recipeData = {
      recipeId: recipe.id,
      ingredients: recipe.ingredients || [], // Fallback to empty array
      // Use the logical OR (||) to provide null if sourceUrl is missing
      sourceUrl: recipe.sourceUrl || null 
    };

    try {
      await setDoc(doc(db, "meals", docId), recipeData, { merge: true });
      console.log("Recipe linked successfully!");
    } catch (err) {
      console.error("Error linking recipe:", err);
    }
  };

  // Handle clicking on a linked recipe
  const handleOpenRecipe = (dateId) => {
    const meal = meals[dateId];
    if (meal?.recipeId) {
      const recipe = recipes.find(r => r.id === meal.recipeId);
      if (recipe) {
        setSelectedRecipeForVault(recipe);
        setIsVaultOpen(true);
      }
    }
  };

  const getSmartMergedIngredients = () => {
    const rawIngredients = [];
    const ignoredKeywords = ['leftovers', 'takeout', 'eating out', 'none'];

    Object.values(meals).forEach(meal => {
      const mainDish = (meal.main || "").toLowerCase().trim();
      
      // Skip this meal if it matches an ignored keyword
      if (ignoredKeywords.includes(mainDish)) return;
      // 1. Add formal ingredients if recipe is linked

      if (meal.ingredients && meal.ingredients.length > 0) {
        rawIngredients.push(...meal.ingredients);
      } 
      // 2. Fallback: If no ingredients, add the meal title (e.g., "Frozen Pizza")
      else if (meal.main && meal.main.trim() !== "") {
        rawIngredients.push(meal.main);
      }

      // 3. Add the Sides (treat them as individual items)
      if (meal.side && meal.side.trim() !== "") {
        // Splits "Fries, Salad" into ["Fries", "Salad"]
        const sideList = meal.side.split(',').map(s => s.trim());
        rawIngredients.push(...sideList);
      }
    });

    const inventory = {};

    rawIngredients.forEach(ing => {
      // Regex fix for the ESLint warning you saw earlier!
      const match = ing.match(/^([\d/.\s?]+)?(.*)/);
      let amount = match[1] ? match[1].trim() : "";
      let name = match[2] ? match[2].trim().toLowerCase() : ing.toLowerCase();

      // Basic pluralization cleanup
      if (name.endsWith('s')) name = name.slice(0, -1);

      if (inventory[name]) {
        const currentAmt = parseFloat(inventory[name].amount) || 0;
        const newAmt = parseFloat(amount) || 0;
        inventory[name].amount = currentAmt + newAmt || "";
      } else {
        inventory[name] = { amount, name };
      }
    });

    return Object.values(inventory).map(item => 
      `${item.amount} ${item.name}${item.amount > 1 ? 's' : ''}`.trim()
    );
  };

  const handleClearDay = async (dateId) => {
    // 1. Update Local State
    setMeals(prev => {
      const newMeals = { ...prev };
      delete newMeals[dateId];
      return newMeals;
    });

    // 2. Update Firebase
    try {
      const docId = `${user.uid}_${dateId}`;
      // We set these to null or empty to "clear" them in the doc
      await setDoc(doc(db, "meals", docId), {
        main: "",
        side: "",
        recipeId: null,
        ingredients: [],
        date: dateId,
        userId: user.uid
      }, { merge: true });
    } catch (err) {
      console.error("Clear failed:", err);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400 font-medium">Fetching the menu...</div>;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="max-w-3xl mx-auto mt-4 md:mt-8 p-3 md:p-4 pb-20">
        <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] shadow-sm border border-gray-100 p-4 md:p-8">
          
          {/* Responsive Header */}
          <header className="flex flex-col gap-4 mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight">Meal Planner</h2>
                <p className="text-gray-400 font-bold text-[10px] md:text-xs uppercase tracking-widest mt-1">
                  Week Starting {currentWeekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </p>
              </div>
              {/* Functional Buttons */}
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsGroceryOpen(true)}
                  className="p-2.5 rounded-xl border border-gray-100 bg-white active:bg-green-50 text-gray-400 active:text-green-600 shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
                <button 
                  onClick={() => setIsVaultOpen(true)}
                  className="p-2.5 rounded-xl border border-gray-100 bg-white active:bg-blue-50 text-gray-400 active:text-blue-600 shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Navigation Bar - Own Row on Mobile */}
            <div className="flex gap-2 w-full">
              <button onClick={handlePrevWeek} className="flex-1 py-2 flex items-center justify-center rounded-xl border border-gray-100 active:bg-gray-50 transition">←</button>
              <button onClick={handleNextWeek} className="flex-[3] py-2 bg-blue-600 text-white rounded-xl font-bold active:bg-blue-700 transition shadow-md shadow-blue-100 text-sm">Next Week</button>
            </div>
          </header>

          <div className="space-y-4 md:space-y-6">
            {getWeekDates().map(dateId => {
              const [y, m, d] = dateId.split('-');
              const dateObj = new Date(y, m - 1, d);
              
              return (
                <div key={dateId} className="flex flex-col md:grid md:grid-cols-4 gap-2 md:gap-4 items-start md:items-center p-3 md:p-4 rounded-2xl hover:bg-slate-50 transition-colors group border border-transparent md:border-none bg-slate-50/30 md:bg-transparent">
                  
                  {/* Date Labels */}
                  <div className="flex md:flex-col items-center md:items-start gap-2 md:gap-0 md:col-span-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase leading-none">{dateObj.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                    <p className="text-xs md:text-sm font-bold text-gray-700">{dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                  </div>

                  {/* Input Area */}
                  <div className="w-full md:col-span-3">
                    <Droppable droppableId={`day-${dateId}`}>
                      {(provided, snapshot) => (
                        <div 
                          ref={provided.innerRef} 
                          {...provided.droppableProps}
                          className={`p-1 rounded-xl transition-all ${snapshot.isDraggingOver ? 'bg-blue-50 ring-2 ring-blue-200' : ''}`}
                        >
                          {/* Draggable opens here, wrapping everything for this day */}
                          <Draggable draggableId={`drag-${dateId}`} index={0}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className={`space-y-2 rounded-xl transition-shadow ${dragSnapshot.isDragging ? 'shadow-xl bg-white' : ''}`}
                              >
                                {/* Main Dish Container */}
                                <div className="relative flex items-center overflow-hidden">
                                  <input
                                    type="text"
                                    className={`w-full p-3 pr-10 rounded-xl text-sm font-bold shadow-sm outline-none transition border-2 ${
                                      meals[dateId]?.recipeId 
                                        ? 'bg-amber-50 border-amber-200 text-amber-900' 
                                        : 'bg-white border-transparent focus:border-blue-100 text-gray-700'
                                    }`}
                                    value={meals[dateId]?.main || ''}
                                    readOnly={!!meals[dateId]?.recipeId}
                                    onClick={() => handleOpenRecipe(dateId)}
                                    onChange={(e) => handleChange(dateId, 'main', e.target.value)}
                                    placeholder="Main dish..."
                                  />
                                  
                                  {/* Right-aligned Buttons */}
                                  <div className="absolute right-2 flex items-center gap-1">
                                    {meals[dateId]?.recipeId && (
                                      <div className="p-1.5 bg-amber-200 text-amber-700 rounded-lg">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                                        </svg>
                                      </div>
                                    )}
                                    {(meals[dateId]?.main || meals[dateId]?.side) && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); handleClearDay(dateId); }}
                                        className="p-1.5 text-gray-300 hover:text-red-500 transition-colors bg-white rounded-lg shadow-sm border border-gray-100"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Side Dish Input (Now correctly grouped inside Draggable!) */}
                                <input
                                  type="text"
                                  className="w-full bg-blue-50/40 border border-transparent p-2 px-3 rounded-lg text-xs font-semibold italic text-blue-600 outline-none transition"
                                  value={meals[dateId]?.side || ''}
                                  onChange={(e) => handleChange(dateId, 'side', e.target.value)}
                                  placeholder="+ Add a side"
                                />
                              </div>
                            )}
                          </Draggable> 
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              );
            })}
          </div>
          
        {/* Modals stay outside */}
        <RecipeVault 
          isOpen={isVaultOpen} 
          onClose={() => {
            setIsVaultOpen(false);
            setSelectedRecipeForVault(null); // Clear it so it can be "re-opened"
          }} 
          recipes={recipes}
          selectedRecipeForVault={selectedRecipeForVault}
          onSelectRecipe={(recipe) => {
            // For now, we'll need a way to tell the vault WHICH day is active
            // Or just make the recipes clickable to "Send to next empty day"
            console.log("Recipe Selected:", recipe);
          }}
        />
        <GroceryDrawer 
          isOpen={isGroceryOpen} 
          onClose={() => setIsGroceryOpen(false)} 
          ingredients={getSmartMergedIngredients()} 
          staples={staples} 
          user={user}
        />
      </div>
    </div>
    </DragDropContext>
  );
};

export default MealPlanner;